import { ReferredUsersTable } from "./ReferredUsersTable.tsx";
import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { useState } from "react";
import { useReferredUsers } from "./useReferredUsers.ts";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Layout } from "../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { Card } from "../UI/Card.tsx";
import { TextButton } from "../UI/TextButton.tsx";
import { ExportCsvModal } from "./ExportCsvModal.tsx";
import { ManuallyEnqueueReferralWonModal } from "./ManuallyEnqueueReferralWonModal.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";

dayjs.extend(utc);
dayjs.extend(timezone);

export const ReferredUsersScreenPath = "/referred-users";

export function ReferredUsersScreen() {
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [inviteeEmail, setInviteeEmail] = useState<string>("");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);

  const convertToUTCStartOfDay = (dateString: string) => {
    if (!dateString) return undefined;
    return dayjs
      .tz(dateString, "Europe/Rome")
      .startOf("day")
      .utc()
      .toISOString();
  };

  const convertToUTCEndOfDay = (dateString: string) => {
    if (!dateString) return undefined;
    return dayjs.tz(dateString, "Europe/Rome").endOf("day").utc().toISOString();
  };

  const { data, isLoading } = useReferredUsers({
    limit,
    offset,
    fromDate: fromDate ? convertToUTCStartOfDay(fromDate) : undefined,
    toDate: toDate ? convertToUTCEndOfDay(toDate) : undefined,
    inviteeEmail: inviteeEmail || undefined,
  });

  const handleNextPage = () => {
    if (data.hasNextPage) {
      setOffset(offset + limit);
    }
  };

  const handlePreviousPage = () => {
    if (data.hasPreviousPage) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  return (
    <Layout>
      <ExportCsvModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      />
      <ManuallyEnqueueReferralWonModal
        isOpen={isManualModalOpen}
        onClose={() => setIsManualModalOpen(false)}
      />
      <Card>
        <div className={"flex items-center justify-between"}>
          <ScreenTitleBlock title={"Referred Users"} />
          <div className={"flex items-center gap-2"}>
            <PrimaryButton
              label={"Add Manual Referral Won"}
              onClick={() => setIsManualModalOpen(true)}
            />
            <PrimaryButton
              label={"Export CSV"}
              onClick={() => setIsExportModalOpen(true)}
            />
          </div>
        </div>
      </Card>
      <div className={"mt-4"}>
        <Card>
          <div className={"mb-4 grid grid-cols-1 md:grid-cols-3 gap-4"}>
            <div>
              <label className={"block text-sm font-medium mb-2"}>
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={"w-full px-3 py-2 border border-gray-300 rounded-md"}
              />
            </div>
            <div>
              <label className={"block text-sm font-medium mb-2"}>
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={"w-full px-3 py-2 border border-gray-300 rounded-md"}
              />
            </div>
            <div>
              <label className={"block text-sm font-medium mb-2"}>
                Invitee Email
              </label>
              <input
                type="email"
                value={inviteeEmail}
                onChange={(e) => setInviteeEmail(e.target.value)}
                placeholder="Filter by invitee email"
                className={"w-full px-3 py-2 border border-gray-300 rounded-md"}
              />
            </div>
          </div>

          {isLoading ? (
            <LoadingRows />
          ) : (
            <div>
              <ReferredUsersTable records={data.records} />

              <div className={"flex w-full justify-between mt-4"}>
                <div>
                  {data.hasPreviousPage ? (
                    <TextButton
                      onClick={handlePreviousPage}
                      label={"Previous page"}
                    />
                  ) : null}
                </div>
                <div>
                  {data.hasNextPage ? (
                    <TextButton onClick={handleNextPage} label={"Next page"} />
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
